import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, Link, useLocation } from 'react-router-dom';
import { getApiBaseUrl } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import CondoNav from './CondoNav';

function CondoLayout({ children }) {
  const { condoSlug } = useParams();
  const location = useLocation();
  const { user, isAuthenticated, isActive, loading: authLoading } = useAuth();
  const [condo, setCondo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchCondo = useCallback(async () => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/api/condos/slug/${condoSlug}`);
      setCondo(response.data.condo);
      return response.data.condo;
    } catch (err) {
      console.error('Failed to fetch condo:', err);
      return null;
    }
  }, [condoSlug]);

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      window.history.replaceState({}, document.title);
      return () => clearTimeout(timer);
    }
  }, [location]);

  useEffect(() => {
    const initializePage = async () => {
      if (authLoading) return;
      await fetchCondo();
      setLoading(false);
    };

    initializePage();
  }, [condoSlug, fetchCondo, authLoading]);

  const hasAccess = !loading && !!condo && isAuthenticated && isActive && !!user && (
    user.admin ||
    user.condos?.some(c =>
      (c.slug === condoSlug) || (c._id === condo._id) || (c === condo._id)
    )
  );

  if (authLoading || loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!condo) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <h1 className="text-3xl font-bold text-foreground mb-4">Condo Not Found</h1>
          <p className="text-muted-foreground mb-6">The condo you're looking for doesn't exist.</p>
          <Link to="/" className="text-primary hover:underline">← Back to Home</Link>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-card rounded-lg shadow-xl p-8 border border-border text-center">
          <h1 className="text-3xl font-bold text-foreground mb-2">{condo.name}</h1>
          <p className="text-muted-foreground mb-6">Please sign in to access this condo</p>
          <div className="space-y-4">
            <Link
              to="/login"
              state={{ from: `${location.pathname}${location.search}` }}
              className="block w-full bg-primary text-primary-foreground py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              Sign In
            </Link>
            <p className="text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary hover:underline">Register here</Link>
            </p>
          </div>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground mt-6 block">
            ← Back to Home
          </Link>
        </div>
      </div>
    );
  }

  if (!isActive) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-card rounded-lg shadow-xl p-8 border border-border text-center">
          <div className="text-6xl mb-4">⏳</div>
          <h1 className="text-3xl font-bold text-foreground mb-4">Account Pending Approval</h1>
          <p className="text-muted-foreground mb-6">
            Your account is waiting for administrator approval. You'll be able to access the condo once approved.
          </p>
          <Link to="/" className="text-primary hover:underline">← Back to Home</Link>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-card rounded-lg shadow-xl p-8 border border-border text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h1 className="text-3xl font-bold text-foreground mb-4">Access Denied</h1>
          <p className="text-muted-foreground mb-6">
            You don't have access to {condo.name}. Please contact an administrator if you believe this is an error.
          </p>
          <Link to="/" className="text-primary hover:underline">← Back to Home</Link>
        </div>
      </div>
    );
  }

  const isAdmin = user?.admin || false;

  return (
    <div className="container mx-auto px-4 py-8 pb-20 md:pb-8">
      <div className="max-w-7xl mx-auto">
        <CondoNav />

        {successMessage && (
          <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded mb-6 mt-6">
            {successMessage}
          </div>
        )}

        {typeof children === 'function'
          ? children({ condo, isAdmin, condoSlug })
          : children}
      </div>
    </div>
  );
}

export default CondoLayout;
